import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useVideoDashboardStore } from '@/store/videoDashboard';
import { useIsMobile } from '@/hooks/use-mobile';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { Video } from '@/store/videoDashboard/types';
import { ChevronDown, Check, Star, Eye, Upload, Clock, Folder as FolderIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';

interface VideoSelectorV2Props {
  value: string;
  onChange: (videoId: string | null) => void;
  placeholder?: string;
  allowPin?: boolean;
  showTabs?: boolean;
  allowClear?: boolean;
  disabled?: boolean;
  className?: string;
  onCreateNew?: () => void;
}

export function VideoSelectorV2({
  value,
  onChange,
  placeholder = translateInterfaceText("Selecione um vídeo..."),
  allowPin = true,
  showTabs = true,
  allowClear = true,
  disabled = false,
  className,
  onCreateNew,
}: VideoSelectorV2Props) {
  useLanguage();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'recent' | 'folders' | 'all'>('recent');
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [previewVideoId, setPreviewVideoId] = useState<string | null>(null);
  
  const [pinnedVideoIds, setPinnedVideoIds] = useState<string[]>(() => {
    if (typeof window === 'undefined') return [];
    return JSON.parse(localStorage.getItem('pinned_videos') || '[]');
  });
  
  const [recentVideoIds, setRecentVideoIds] = useState<string[]>(() => {
    if (typeof window === 'undefined') return [];
    return JSON.parse(localStorage.getItem('recent_videos') || '[]');
  });

  const { videos, folders } = useVideoDashboardStore();
  const debouncedSearch = useDebouncedValue(searchQuery, 250);

  // Filter only available videos (processed or active — backend may return 'active' for ready videos)
  const processedVideos = useMemo(() => {
    return videos.filter(v => v.status === 'processed' || v.status === 'active' || !v.status);
  }, [videos]);

  // Filter videos based on search and active tab
  const filteredVideos = useMemo(() => {
    let result = [...processedVideos];
    
    // Search filter
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase();
      result = result.filter(v => 
        v.title.toLowerCase().includes(query) ||
        v.tags?.some(tag => tag.toLowerCase().includes(query)) ||
        v.description?.toLowerCase().includes(query)
      );
    }
    
    // Tab filter - only apply when tabs are visible
    if (showTabs) {
      if (activeTab === 'recent') {
        result = result.filter(v => 
          pinnedVideoIds.includes(v.id) || recentVideoIds.includes(v.id)
        );
        // Sort: pinned first, then by recency
        result.sort((a, b) => {
          const aPinned = pinnedVideoIds.includes(a.id);
          const bPinned = pinnedVideoIds.includes(b.id);
          if (aPinned && !bPinned) return -1;
          if (!aPinned && bPinned) return 1;
          const aIndex = recentVideoIds.indexOf(a.id);
          const bIndex = recentVideoIds.indexOf(b.id);
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });
      } else if (activeTab === 'folders' && selectedFolderId) {
        result = result.filter(v => v.folderId === selectedFolderId);
      }
    }
    
    return result;
  }, [processedVideos, debouncedSearch, activeTab, selectedFolderId, pinnedVideoIds, recentVideoIds, showTabs]);

  const selectedVideo = processedVideos.find(v => v.id === value);

  // Handle selection
  const handleSelect = (videoId: string) => {
    onChange(videoId);
    setOpen(false);
    
    // Update recent videos (max 10)
    const newRecents = [videoId, ...recentVideoIds.filter(id => id !== videoId)].slice(0, 10);
    setRecentVideoIds(newRecents);
    localStorage.setItem('recent_videos', JSON.stringify(newRecents));
  };

  // Handle pin toggle
  const handleTogglePin = (videoId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newPinned = pinnedVideoIds.includes(videoId)
      ? pinnedVideoIds.filter(id => id !== videoId)
      : [...pinnedVideoIds, videoId];
    setPinnedVideoIds(newPinned);
    localStorage.setItem('pinned_videos', JSON.stringify(newPinned));
  };

  // Handle clear
  const handleClear = () => {
    onChange(null);
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const content = (
    <VideoSelectorContent
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      filteredVideos={filteredVideos}
      selectedVideo={selectedVideo}
      handleSelect={handleSelect}
      handleTogglePin={handleTogglePin}
      pinnedVideoIds={pinnedVideoIds}
      allowPin={allowPin}
      showTabs={showTabs}
      allowClear={allowClear}
      handleClear={handleClear}
      formatDuration={formatDuration}
      folders={folders}
      selectedFolderId={selectedFolderId}
      setSelectedFolderId={setSelectedFolderId}
      onCreateNew={onCreateNew}
    />
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>
          <Button 
            variant="outline" 
            className={cn("w-full justify-between group bg-white border-[#E5E7EB] text-[#374151] hover:bg-white hover:text-[#374151] hover:border-[#E5E7EB] transition-colors overflow-hidden", className)}
            disabled={disabled}
          >
            {selectedVideo ? (
              <div className="flex items-center gap-2 min-w-0 overflow-hidden flex-1">
                <img 
                  src={selectedVideo.thumbnailUrl} 
                  className="w-6 h-6 rounded object-cover flex-shrink-0"
                  alt=""
                />
                <span className="truncate text-left">{selectedVideo.title}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </DrawerTrigger>
        <DrawerContent className="max-h-[55vh]">
          <div className="px-4 pb-4 max-w-full overflow-hidden">
            {content}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          className={cn("w-full justify-between group bg-white border-[#E5E7EB] text-[#374151] hover:bg-white hover:text-[#374151] hover:border-[#E5E7EB] transition-colors", className)}
          disabled={disabled}
        >
          {selectedVideo ? (
            <div className="flex items-center gap-2 min-w-0 overflow-hidden flex-1">
              <img 
                src={selectedVideo.thumbnailUrl} 
                className="w-6 h-6 rounded object-cover flex-shrink-0"
                alt=""
              />
              <span className="truncate text-left">{selectedVideo.title}</span>
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
        <PopoverContent
          className={cn(
            "p-0",
            "w-[var(--radix-popover-trigger-width)]",
            "min-w-[280px]",
            "border border-[#E6E8EC]",
            "rounded-[10px]",
            "shadow-[0_8px_22px_rgba(0,0,0,0.08)]",
            "animate-scale-in",
            "overflow-hidden"
          )}
          align="start"
          side="bottom"
          sideOffset={8}
          avoidCollisions={true}
          collisionPadding={12}
          style={{ maxHeight: 'min(420px, calc(100vh - 120px))' }}
        >
        {content}
      </PopoverContent>
    </Popover>
  );
}

function VideoSelectorContent({
  searchQuery,
  setSearchQuery,
  activeTab,
  setActiveTab,
  filteredVideos,
  selectedVideo,
  handleSelect,
  handleTogglePin,
  pinnedVideoIds,
  allowPin,
  showTabs,
  allowClear,
  handleClear,
  formatDuration,
  folders,
  selectedFolderId,
  setSelectedFolderId,
  onCreateNew,
}: any) {
  useLanguage();
  return (
    <Command className="rounded-lg border-0 overflow-hidden flex flex-col">
      {/* Search */}
      <CommandInput 
        placeholder={showTabs ? translateInterfaceText("Buscar por título, pasta ou tag...") : translateInterfaceText("Buscar vídeo...")}
        value={searchQuery}
        onValueChange={setSearchQuery}
      />
      
      {/* Tabs */}
      {showTabs && (
        <div className="border-b px-3 py-2">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full grid grid-cols-3">
              <TabsTrigger 
                value="recent" 
                className="data-[state=active]:bg-[#2979FF] data-[state=active]:text-white data-[state=active]:shadow-sm hover:bg-[#EBF4FF] hover:text-[#2979FF]"
              >
                {translateInterfaceText("Recentes")}</TabsTrigger>
              <TabsTrigger 
                value="folders"
                className="data-[state=active]:bg-[#2979FF] data-[state=active]:text-white data-[state=active]:shadow-sm hover:bg-[#EBF4FF] hover:text-[#2979FF]"
              >
                {translateInterfaceText("Pastas")}</TabsTrigger>
              <TabsTrigger 
                value="all"
                className="data-[state=active]:bg-[#2979FF] data-[state=active]:text-white data-[state=active]:shadow-sm hover:bg-[#EBF4FF] hover:text-[#2979FF]"
              >
                {translateInterfaceText("Todos")}</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}
      
      {/* Content */}
      {showTabs && activeTab === 'folders' ? (
        <FoldersTabContent 
          folders={folders}
          selectedFolderId={selectedFolderId}
          setSelectedFolderId={setSelectedFolderId}
          videos={filteredVideos}
          onSelect={handleSelect}
          selectedVideoId={selectedVideo?.id}
          pinnedVideoIds={pinnedVideoIds}
          onTogglePin={handleTogglePin}
          allowPin={allowPin}
          formatDuration={formatDuration}
        />
      ) : (
        <CommandList className="max-h-[280px] overflow-y-auto">
          <CommandEmpty>
            <EmptyState 
              searchQuery={searchQuery}
              onCreateNew={onCreateNew}
            />
          </CommandEmpty>
          
          <CommandGroup>
            {filteredVideos.map((video: Video) => (
              <VideoItem
                key={video.id}
                video={video}
                isSelected={selectedVideo?.id === video.id}
                isPinned={pinnedVideoIds.includes(video.id)}
                onSelect={handleSelect}
                onTogglePin={handleTogglePin}
                allowPin={allowPin}
                formatDuration={formatDuration}
              />
            ))}
          </CommandGroup>
        </CommandList>
      )}
    </Command>
  );
}

function FoldersTabContent({
  folders,
  selectedFolderId,
  setSelectedFolderId,
  videos,
  onSelect,
  selectedVideoId,
  pinnedVideoIds,
  onTogglePin,
  allowPin,
  formatDuration,
}: any) {
  useLanguage();
  const userFolders = folders.filter((f: any) => !['all', 'recent'].includes(f.id));
  
  return (
    <div className="flex h-[320px]">
      {/* Folders list */}
      <ScrollArea className="w-[220px] border-r">
        <div className="p-2 space-y-1">
          {userFolders.map((folder: any) => (
            <Button
              key={folder.id}
              variant={selectedFolderId === folder.id ? "secondary" : "ghost"}
              className="w-full justify-start"
              onClick={() => setSelectedFolderId(folder.id)}
            >
              <span className="truncate flex-1 text-left">{folder.name}</span>
              <Badge variant="secondary" className="ml-auto">
                {folder.count}
              </Badge>
            </Button>
          ))}
        </div>
      </ScrollArea>
      
      {/* Videos in folder */}
      <ScrollArea className="flex-1">
        {selectedFolderId ? (
          <div className="p-2">
            {videos.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                {translateInterfaceText("Nenhum vídeo nesta pasta")}</div>
            ) : (
              videos.map((video: Video) => (
                <VideoItem
                  key={video.id}
                  video={video}
                  isSelected={selectedVideoId === video.id}
                  isPinned={pinnedVideoIds.includes(video.id)}
                  onSelect={onSelect}
                  onTogglePin={onTogglePin}
                  allowPin={allowPin}
                  formatDuration={formatDuration}
                />
              ))
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            {translateInterfaceText("Selecione uma pasta")}</div>
        )}
      </ScrollArea>
    </div>
  );
}

function VideoItem({
  video,
  isSelected,
  isPinned,
  onSelect,
  onTogglePin,
  allowPin,
  formatDuration,
}: any) {
  useLanguage();
  const [showPreview, setShowPreview] = useState(false);

  return (
    <CommandItem
      value={video.id}
      onSelect={() => onSelect(video.id)}
      className="h-[44px] px-3 cursor-pointer group transition-colors duration-150 hover:bg-[#E8F2FF] aria-selected:bg-[#1D4FFF] aria-selected:text-white"
    >
      <div className="flex items-center gap-3 w-full">
        {/* Thumbnail - 32px */}
        <TooltipProvider delayDuration={300}>
          <Tooltip open={showPreview}>
            <TooltipTrigger asChild>
              <div 
                className="relative w-8 h-8 flex-shrink-0 rounded-md overflow-hidden bg-muted border border-[rgba(0,0,0,0.06)]"
                onMouseEnter={() => setShowPreview(true)}
                onMouseLeave={() => setShowPreview(false)}
              >
                <img 
                  src={video.thumbnailUrl} 
                  alt={video.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="w-[280px]">
              <VideoPreviewTooltip video={video} formatDuration={formatDuration} />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold truncate">{video.title}</h4>
            {video.status === 'processing' && (
              <Badge variant="secondary" className="text-xs">{translateInterfaceText("Processando")}</Badge>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-1">
          {isSelected && (
            <Check className="h-4 w-4 text-white" />
          )}
        </div>
      </div>
    </CommandItem>
  );
}

function VideoPreviewTooltip({ video, formatDuration }: any) {
  useLanguage();
  return (
    <div className="space-y-2">
      <div className="aspect-video rounded-md overflow-hidden bg-muted">
        <img 
          src={video.thumbnailUrl} 
          alt={video.title}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="space-y-1">
        <h4 className="font-semibold text-sm">{video.title}</h4>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            <span>{formatDuration(video.duration || 0)}</span>
          </div>
          {video.createdAt && (
            <>
              <span>•</span>
              <span>{new Date(video.createdAt).toLocaleDateString(getInterfaceLanguage())}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ searchQuery, onCreateNew }: any) {
  useLanguage();
  return (
    <div className="text-center py-8 px-4">
      {searchQuery ? (
        <>
          <h3 className="text-sm font-semibold mb-2">
            {translateInterfaceText("Nenhum vídeo encontrado para \"")}{searchQuery}"
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            {translateInterfaceText("Tente verificar a ortografia ou usar palavras-chave diferentes")}</p>
        </>
      ) : (
        <>
          <h3 className="text-sm font-semibold mb-2">
            {translateInterfaceText("Nenhum vídeo disponível")}</h3>
          <p className="text-xs text-muted-foreground mb-4">
            {translateInterfaceText("Faça upload do primeiro vídeo para começar")}</p>
        </>
      )}
      {onCreateNew && (
        <Button size="sm" onClick={onCreateNew}>
          <Upload className="w-4 h-4 mr-2" />
          {translateInterfaceText("Criar Novo Vídeo")}</Button>
      )}
    </div>
  );
}
